import React, { useEffect, useState } from "react";
import "../Styles/PayScreen.css";
import { QRCode } from "react-qrcode-logo";
import { useParams } from "react-router-dom";
import db from "../firebase";
import { collection, doc, getDoc, setDoc } from "firebase/firestore";

// SVG Icons as components for better organization
const WalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M20 21a8 8 0 0 0-16 0" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const PayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

function PayScreen() {
  const { document_id } = useParams();

  const [upiId, setUpiId] = useState("");
  const [username, setUsername] = useState("");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [upiLink, setUpiLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const pay_amount = async () => {
    setProcessing(true);
    const pay_click_ref = collection(db, "pay_clicks");

    await setDoc(doc(pay_click_ref), {
      upi_id: upiId,
      username,
      amount,
      message,
      link: upiLink,
      generated_link_id: document_id,
      timestamp: new Date(),
    })
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
        setProcessing(false);
      });

    window.location.replace(upiLink);
  };

  useEffect(() => {
    const docRef = doc(db, "successful_links", document_id);
    getDoc(docRef)
      .then((response) => {
        if (response.exists()) {
          setUpiId(response.data().upi_id);
          setUpiLink(response.data().link);
          setUsername(response.data().username);
          setAmount(response.data().amount);
          setMessage(response.data().message);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching document:", error);
        setLoading(false);
      });

    return () => { };
  }, [document_id]);

  // Format amount with commas for Indian numbering system
  const formatAmount = (value) => {
    if (!value) return "0";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString('en-IN');
  };

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading payment details...</p>
      </div>
    );
  }

  return (
    <div className="pay-screen">
      <div className="pay-container">
        {/* Header */}
        <header className="pay-header">
          <div className="pay-logo">
            <img src="/app_icon.png" alt="ShoPay" />
          </div>
          <h1>Payment Request</h1>
          <p>Scan the QR or tap Pay Now to proceed</p>
        </header>

        {/* Main Card */}
        <div className="pay-card">
          {/* Amount Section */}
          <div className="amount-section">
            <span className="amount-label">Amount to Pay</span>
            <div className="amount-value">
              <span className="currency">₹</span>
              {formatAmount(amount)}
            </div>
          </div>

          {/* Details Section */}
          <div className="details-section">
            <div className="detail-item">
              <div className="detail-icon">
                <WalletIcon />
              </div>
              <div className="detail-content">
                <span className="detail-label">UPI ID</span>
                <span className="detail-value">{upiId || "—"}</span>
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon">
                <UserIcon />
              </div>
              <div className="detail-content">
                <span className="detail-label">Payee Name</span>
                <span className="detail-value">{username || "—"}</span>
              </div>
            </div>

            {message && (
              <div className="detail-item">
                <div className="detail-icon">
                  <MessageIcon />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Note</span>
                  <span className="detail-value">{message}</span>
                </div>
              </div>
            )}
          </div>

          {/* QR Code Section */}
          <div className="qr-section">
            <div className="qr-title">Scan to Pay</div>
            <div className="qr-wrapper">
              <QRCode
                value={upiLink}
                size={180}
                quietZone={0}
                bgColor="#ffffff"
                fgColor="#1a1a2e"
                qrStyle="dots"
                eyeRadius={8}
              />
            </div>
          </div>

          {/* Pay Button */}
          <div className="pay-button-section">
            <button
              onClick={pay_amount}
              className="pay-button"
              disabled={processing}
            >
              <span className="pay-button-content">
                {processing ? (
                  <>Processing...</>
                ) : (
                  <>
                    <PayIcon />
                    Pay Now
                  </>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Security Badge */}
        <div className="security-badge">
          <ShieldIcon />
          <span>Secured by UPI • End-to-end encrypted</span>
        </div>

        {/* App Download Section */}
        <div className="app-download-section">
          <p className="download-title">Get the App</p>
          <div className="download-buttons">
            <a
              href="https://apps.apple.com/my/app/shopay-upi-qr-maker/id6756309315"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn ios-btn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div className="btn-text">
                <span className="btn-label">Download on the</span>
                <span className="btn-store">App Store</span>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.shoaib.upigenerator.upi_generator"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn android-btn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <div className="btn-text">
                <span className="btn-label">Get it on</span>
                <span className="btn-store">Google Play</span>
              </div>
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="pay-footer">
          <p>Powered by</p>
          <a href="https://getshopay.com" target="_blank" rel="noopener noreferrer">
            <img src="/wide_logo.png" alt="ShoPay" className="footer-logo" />
          </a>
        </footer>
      </div>
    </div>
  );
}

export default PayScreen;
